import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheckCircle, FiZap, FiUsers, FiCheckSquare } from 'react-icons/fi';

const WelcomeAnimation = ({ user, onComplete }) => {
  const [step, setStep] = useState(0);
  const [showParticles, setShowParticles] = useState(true);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;

  useEffect(() => {
    // Step 1: Welcome message
    const timer1 = setTimeout(() => setStep(1), 300);
    // Step 2: Show features
    const timer2 = setTimeout(() => setStep(2), 1200);
    // Step 3: Complete and fade out
    const timer3 = setTimeout(() => {
      setStep(3);
      setShowParticles(false);
    }, 2500);
    // Step 4: Call onComplete
    const timer4 = setTimeout(() => {
      if (onComplete) onComplete();
    }, 3500);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, [onComplete]);

  // Particle animation
  const particles = Array.from({ length: isMobile ? 30 : 50 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    delay: Math.random() * 1.5,
    duration: 2 + Math.random() * 1,
    size: 4 + Math.random() * 4,
  }));

  const features = [
    { icon: FiCheckSquare, text: 'Manage Tasks', color: '#3b82f6' },
    { icon: FiUsers, text: 'Team Collaboration', color: '#10b981' },
    { icon: FiZap, text: 'Stay Productive', color: '#f59e0b' },
  ];

  return (
    <div className="fixed inset-0 z-[300] bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700 flex items-center justify-center overflow-hidden">
      {/* Animated background particles */}
      <AnimatePresence>
        {showParticles && (
          <div className="absolute inset-0">
            {particles.map((particle) => (
              <motion.div
                key={particle.id}
                initial={{
                  x: `${particle.x}vw`,
                  y: `${particle.y}vh`,
                  opacity: 0,
                  scale: 0,
                }}
                animate={{
                  opacity: [0, 1, 1, 0],
                  scale: [0, 1, 1, 0],
                  y: `${particle.y + 20}vh`,
                }}
                transition={{
                  duration: particle.duration,
                  delay: particle.delay,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
                className="absolute rounded-full"
                style={{
                  width: `${particle.size}px`,
                  height: `${particle.size}px`,
                  background: 'rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 0 10px rgba(255, 255, 255, 0.5)',
                }}
              />
            ))}
          </div>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="relative z-10 text-center px-4">
        {/* Welcome message */}
        <AnimatePresence>
          {step >= 1 && (
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ type: 'spring', damping: 15, stiffness: 200 }}
              className="mb-8"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: 'spring', stiffness: 200, damping: 10 }}
                className="inline-block mb-6"
              >
                <div className="w-28 h-28 sm:w-40 sm:h-40 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center border-4 border-white/30 shadow-2xl overflow-hidden">
                  <img 
                    src="/logo.png" 
                    alt="TaskMate Logo" 
                    className="h-20 w-20 sm:h-28 sm:w-28 object-contain"
                    onError={(e) => {
                      // Fallback to icon if logo not found
                      e.target.style.display = 'none';
                      const icon = e.target.parentElement.querySelector('svg');
                      if (icon) icon.style.display = 'block';
                    }}
                  />
                  <FiCheckCircle className="text-white hidden" size={isMobile ? 40 : 50} />
                </div>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-3"
              >
                Welcome Back!
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="text-xl sm:text-2xl text-white/90 font-medium"
              >
                {user?.name || 'User'}
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Features animation */}
        <AnimatePresence>
          {step >= 2 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-wrap justify-center gap-4 sm:gap-6 mt-8"
            >
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30, scale: 0.8 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{
                      delay: 1.2 + index * 0.15,
                      type: 'spring',
                      stiffness: 200,
                      damping: 15,
                    }}
                    whileHover={{ scale: 1.1, y: -5 }}
                    className="bg-white/10 backdrop-blur-md rounded-xl p-4 sm:p-5 border border-white/20 shadow-lg"
                  >
                    <Icon
                      className="mx-auto mb-2"
                      size={isMobile ? 24 : 28}
                      style={{ color: feature.color }}
                    />
                    <p className="text-white/90 text-sm sm:text-base font-medium">
                      {feature.text}
                    </p>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Loading indicator */}
        <AnimatePresence>
          {step >= 3 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-8"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full mx-auto"
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Expanding circle effect */}
      <motion.div
        initial={{ scale: 0, opacity: 1 }}
        animate={{ scale: 4, opacity: 0 }}
        transition={{ duration: 1.5, ease: 'easeOut' }}
        className="absolute inset-0 rounded-full bg-white/10"
        style={{ borderRadius: '50%' }}
      />
    </div>
  );
};

export default WelcomeAnimation;
